/**
 * Premium "Logo Liquid Reveal" intro.
 *
 * Visual design:
 *  - Big accent-colored "ink" blob expands from a corner using SVG goo
 *    (feGaussianBlur + feColorMatrix). When the blob fills the frame, an
 *    SVG mask reveals the brand logo + headline as the blob washes back.
 *  - A few smaller satellite drops add organic feel.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const logoLiquidReveal: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const logoH = isVertical ? 200 : 170;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const blobLayerId = `${clipPrefix}-blobs`;
  const mainBlobId = `${clipPrefix}-blob`;
  const dropAId = `${clipPrefix}-drop-a`;
  const dropBId = `${clipPrefix}-drop-b`;
  const dropCId = `${clipPrefix}-drop-c`;
  const contentId = `${clipPrefix}-content`;
  const logoId = `${clipPrefix}-logo`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const gooId = `${clipPrefix}-goo`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  // Goo SVG filter — applied to the blob layer for liquid merge effect.
  const gooFilter = `<svg width="0" height="0" style="position:absolute;">
  <defs>
    <filter id="${gooId}">
      <feGaussianBlur in="SourceGraphic" stdDeviation="20" result="blur" />
      <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -10" result="goo" />
      <feBlend in="SourceGraphic" in2="goo" />
    </filter>
  </defs>
</svg>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  ${gooFilter}

  <div id="${blobLayerId}" style="position:absolute;inset:0;filter:url(#${gooId});pointer-events:none;z-index:3;">
    <div id="${mainBlobId}" style="position:absolute;left:50%;top:50%;width:280px;height:280px;margin-left:-140px;margin-top:-140px;border-radius:9999px;background:${accent};box-shadow:0 0 80px ${withAlpha(accent, 0.6)};"></div>
    <div id="${dropAId}" style="position:absolute;left:30%;top:35%;width:90px;height:90px;border-radius:9999px;background:${accent};opacity:0;"></div>
    <div id="${dropBId}" style="position:absolute;left:65%;top:42%;width:80px;height:80px;border-radius:9999px;background:${accent};opacity:0;"></div>
    <div id="${dropCId}" style="position:absolute;left:50%;top:62%;width:70px;height:70px;border-radius:9999px;background:${accent};opacity:0;"></div>
  </div>

  <div id="${contentId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;z-index:4;">
    <div id="${logoId}" style="filter:drop-shadow(0 12px 30px ${withAlpha(accent, 0.55)});opacity:0;">
      ${brandLogoImg({ id: `${logoId}-img`, sizePx: logoH })}
    </div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${mainBlobId}", { scale: 0.2, opacity: 1 });`,
    `gsap.set("#${dropAId}", { scale: 0.4, opacity: 0 });`,
    `gsap.set("#${dropBId}", { scale: 0.4, opacity: 0 });`,
    `gsap.set("#${dropCId}", { scale: 0.4, opacity: 0 });`,
    `gsap.set("#${logoId}", { opacity: 0, scale: 0.6 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 24 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Drops appear and merge into main blob (goo filter handles the visual merge).
    t.fromTo(`#${dropAId}`, { opacity: 0.95, scale: 1, x: -180, y: -120 }, { x: 0, y: 0, scale: 1.4, duration: 0.65, ease: "raw:\"power2.in\"" }, 0.05),
    t.fromTo(`#${dropBId}`, { opacity: 0.95, scale: 1, x: 200, y: -100 }, { x: 0, y: 0, scale: 1.4, duration: 0.7, ease: "raw:\"power2.in\"" }, 0.1),
    t.fromTo(`#${dropCId}`, { opacity: 0.95, scale: 1, x: 0, y: 200 }, { x: 0, y: 0, scale: 1.4, duration: 0.6, ease: "raw:\"power2.in\"" }, 0.15),
    // Main blob expands to fill.
    t.to(`#${mainBlobId}`, { scale: 14, duration: 0.85, ease: "raw:\"power3.in\"" }, 0.45),
    // Drops fade once merged.
    t.to(`#${dropAId}`, { opacity: 0, duration: 0.2, ease: "raw:\"none\"" }, 0.7),
    t.to(`#${dropBId}`, { opacity: 0, duration: 0.2, ease: "raw:\"none\"" }, 0.78),
    t.to(`#${dropCId}`, { opacity: 0, duration: 0.2, ease: "raw:\"none\"" }, 0.72),
    // Blob recedes back, revealing content beneath.
    t.to(`#${mainBlobId}`, { scale: 0.05, opacity: 0, duration: 0.65, ease: "raw:\"power3.out\"" }, 1.25),
    // Content reveals.
    t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.5)\"" }, 1.4),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.6),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.85));

  // Subtle dwell breathing.
  const dwell = Math.max(0.6, clipDurationSec - 2.0);
  tweens.push(t.to(`#${logoId}`, { scale: 1.05, duration: dwell, ease: "raw:\"sine.inOut\"" }, 2.0));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
